import type { ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { Eye, Flame } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useMarketplaceAccess } from "@/lib/marketplace/flags";
import { useT } from "@/i18n";

/** Amber ribbon shown to admins while Marketplace is in private preview. */
export function MarketplacePreviewRibbon() {
  const { t } = useT();
  return (
    <div className="flex items-center justify-center gap-2 bg-amber-500/15 px-4 py-1.5 text-center text-xs font-semibold text-amber-600 dark:text-amber-400">
      <Eye className="size-3.5" />
      {t("mpcopy.preview.ribbon")}
    </div>
  );
}

function MarketplaceNotFound() {
  const { t } = useT();
  return (
    <div className="flex min-h-[60vh] items-center justify-center px-4">
      <div className="max-w-md text-center">
        <Flame className="mx-auto size-10 text-muted-foreground/40" />
        <h1 className="mt-4 font-display text-2xl font-bold">{t("mpcopy.preview.not_found_title")}</h1>
        <p className="mt-2 text-sm text-muted-foreground">{t("mpcopy.preview.not_found_body")}</p>
        <Button asChild variant="hot" className="mt-6">
          <Link to="/">{t("mp.gate.back_home")}</Link>
        </Button>
      </div>
    </div>
  );
}

/**
 * Gates a Marketplace route. While `marketplace_public_enabled` is false,
 * only users with the marketplace.preview permission see the page
 * (under a preview ribbon); everyone else gets a 404-style response.
 */
export function MarketplaceGate({ children }: { children: ReactNode }) {
  const { isLoading, allowed, isPublic } = useMarketplaceAccess();

  if (isLoading) {
    return (
      <div className="mx-auto max-w-6xl space-y-4 px-4 py-12">
        <Skeleton className="h-10 w-2/3" />
        <Skeleton className="h-5 w-1/2" />
        <div className="grid gap-4 pt-6 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-48" />
          ))}
        </div>
      </div>
    );
  }

  if (!allowed) return <MarketplaceNotFound />;

  return (
    <>
      {!isPublic && <MarketplacePreviewRibbon />}
      {children}
    </>
  );
}
